import { Medical } from "@/clients/models/Medical";
import { QueryKeys } from "@/constants/query-keys";
import { useGetAllergyOptions } from "@/hooks/requests/medical/use-get-allergy-options";
import { useGetMedical } from "@/hooks/requests/medical/use-get-medical";
import { useUpdateMedical } from "@/hooks/requests/medical/use-update-medical";
import { useToast } from "@/hooks/ui/use-toast";
import { setProfileContext } from "@/utils/sentry/tools";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

export const useAllergies = (onSuccess: () => void) => {
  const { data: allergyOptions, isLoading: isOptionsLoading } =
    useGetAllergyOptions();
  const queryClient = useQueryClient();
  const { medical, isLoading: isMedicalLoading } = useGetMedical();
  const { updateMedical, isUpdating } = useUpdateMedical();
  const { showError } = useToast();
  const [allergySet, setAllergySet] = useState<Set<string>>(new Set());
  const [selectedAllergies, setSelectedAllergies] = useState<Set<string>>(
    new Set(),
  );
  const isLoading = isOptionsLoading || isMedicalLoading;
  const sortedAllergyList = useMemo(() => {
    const newArray = Array.from(allergySet);
    newArray.sort();
    return newArray;
  }, [allergySet]);

  useEffect(() => {
    if (allergyOptions) {
      setAllergySet((prev) => {
        const newSet = new Set(prev);
        allergyOptions.allergyOptions.forEach((allergy) => newSet.add(allergy));
        return newSet;
      });
    }
  }, [allergyOptions]);

  useEffect(() => {
    if (medical) {
      setAllergySet((prev) => {
        const newSet = new Set(prev);
        medical.allergies?.forEach((allergy) => newSet.add(allergy));
        return newSet;
      });
      setSelectedAllergies(new Set(medical.allergies ?? []));
    }
  }, [medical]);

  const handleSubmit = () => {
    // TODO: change the allergies to arrays
    const dataBody: Medical = {
      medicalConditions: medical?.medicalConditions ?? [],
      allergies: Array.from(selectedAllergies),
    };

    updateMedical(dataBody, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.MEDICAL] });
        setProfileContext(QueryKeys.MEDICAL, dataBody);
        onSuccess();
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  };

  return {
    allergySet,
    sortedAllergyList,
    allergyOptions,
    setAllergySet,
    selectedAllergies,
    setSelectedAllergies,
    handleSubmit,
    isUpdating,
    isLoading,
  };
};
